import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, ɵresetCompiledComponents } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { LoaderService } from '../../services/loader.service';
import Swal from 'sweetalert2';
import { Subscription } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { Tooltip } from 'bootstrap';
import * as XLSX from 'xlsx';

export interface ReserveData {
  id: string;
  userId: string;
  projectId: string;
  shares: number;
  amount: number; // Se calculará como shares * (priceToken / 100)
  timestamp: number;
  status: 'PENDIENTE' | 'MINTING' | 'FAILED' | 'MINTED' | 'MINTED_FAILED';
  reason?: 'ERROR' | 'MINTING_FAILED' | 'TIMEOUT';
  errorMessage?: string; // Mensaje de error detallado
  projectName?: string;
  priceToken?: number; // Precio del token en centavos
  transferId?: string;
  isExternal?: boolean;
  projectWallet?: string;
  userWallet?: string;
  userEmail?: string; // Email obtenido desde la colección users
  adminNotes?: string; // Notas del administrador
  showDetails?: boolean; // Para controlar el desplegable de detalles
  originFunds?: string;
}

@Component({
  selector: 'app-reserves-management',
  templateUrl: './reserves-management.component.html',
  styleUrls: ['./reserves-management.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ReservesManagementComponent implements OnInit, OnDestroy, AfterViewInit {
  paginatedReserves: ReserveData[] = [];
  
  // Paginación
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 1;
  totalReserves: number = 0; // Total de reservas en Firebase (para paginación)
  
  // Filtros
  filterUID: string = '';
  filterProjectId: string = '';
  filterMinAmount: number | null = null;
  filterMaxAmount: number | null = null;
  filterStatus: string = 'all'; // 'all', 'PENDIENTE', 'FAILED', 'MINTED', 'MINTED_FAILED'
  sortOrder: 'asc' | 'desc' = 'desc';
  
  private reservesSubscription?: Subscription;
  private userEmailsCache: Map<string, string> = new Map();
  private projectInfoCache: Map<string, { name: string | undefined; priceToken: number | undefined }> = new Map();
  private loadedReservesCache: Map<string, ReserveData> = new Map(); // Cache de reservas ya cargadas
  private pageCursors: Map<number, any> = new Map(); // Cursors para navegación de páginas
  private tooltips: Tooltip[] = []; // Array para almacenar instancias de tooltips
  processingReserveIds: Set<string> = new Set(); // IDs de reservas en proceso (anti-doble-click)

  constructor(
    private afs: AngularFirestore,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private fns: AngularFireFunctions
  ) {}

  ngOnInit(): void {
    this.loadReserves();
  }

  ngAfterViewInit(): void {
    // Inicializar tooltips después de que la vista esté lista
    setTimeout(() => {
      this.initializeTooltips();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.reservesSubscription) {
      this.reservesSubscription.unsubscribe();
    }
    this.destroyTooltips();
  }

  private initializeTooltips(): void {
    this.destroyTooltips();
    setTimeout(() => {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      this.tooltips = Array.from(tooltipTriggerList).map(element => {
        return new Tooltip(element, {
          trigger: 'hover',
          container: 'body',
          html: true,
          placement: 'top'
        });
      });
    }, 50);
  }

  private destroyTooltips(): void {
    this.tooltips.forEach(tooltip => tooltip.dispose());
    this.tooltips = [];
  }

  async loadReserves(): Promise<void> {
    // Cargar solo el total de reservas primero para calcular páginas
    await this.loadTotalCount();
    // Cargar la primera página
    await this.loadPage(this.currentPage);
  }

  async loadTotalCount(): Promise<void> {
    try {
      let query = this.afs.collection('reserves', ref => {
        let q: any = ref;
        
        // Aplicar filtro de projectId si está activo
        if (this.filterProjectId.trim()) {
          q = q.where('projectId', '==', this.filterProjectId.trim());
        }
        
        return q;
      });
      
      // Obtener el total de reservas según el filtro de estado activo
      if (this.filterStatus === 'FAILED') {
        // Para FAILED, contar solo las FAILED puras (excluyendo MINTING_FAILED)
        const failedQuery = this.afs.collection('reserves', ref => {
          let q: any = ref.where('status', '==', 'FAILED');
          if (this.filterProjectId.trim()) {
            q = q.where('projectId', '==', this.filterProjectId.trim());
          }
          return q;
        });
        const failedSnapshot = await firstValueFrom(failedQuery.get());
        // Filtrar para contar solo las que NO tienen reason MINTING_FAILED
        this.totalReserves = failedSnapshot.docs.filter((doc: any) => {
          const data = doc.data() as any;
          return data.reason !== 'MINTING_FAILED';
        }).length;
      } else if (this.filterStatus === 'MINTED_FAILED') {
        // Para MINTED_FAILED, buscar FAILED con reason MINTING_FAILED solamente
        const failedQuery = this.afs.collection('reserves', ref => {
          let q: any = ref.where('status', '==', 'FAILED');
          if (this.filterProjectId.trim()) {
            q = q.where('projectId', '==', this.filterProjectId.trim());
          }
          return q;
        });
        const failedSnapshot = await firstValueFrom(failedQuery.get());
        // Filtrar para contar solo las que tienen reason MINTING_FAILED
        this.totalReserves = failedSnapshot.docs.filter((doc: any) => {
          const data = doc.data() as any;
          return data.reason === 'MINTING_FAILED';
        }).length;
      } else if (this.filterStatus !== 'all') {
        const statusQuery = this.afs.collection('reserves', ref => {
          let q: any = ref.where('status', '==', this.filterStatus);
          if (this.filterProjectId.trim()) {
            q = q.where('projectId', '==', this.filterProjectId.trim());
          }
          return q;
        });
        const snapshot = await firstValueFrom(statusQuery.get());
        this.totalReserves = snapshot.size;
      } else {
        // Sin filtro de estado, pero puede tener filtro de projectId
        if (this.filterProjectId.trim()) {
          const projectQuery = this.afs.collection('reserves', ref => 
            ref.where('projectId', '==', this.filterProjectId.trim())
          );
          const snapshot = await firstValueFrom(projectQuery.get());
          this.totalReserves = snapshot.size;
        } else {
          // Sin filtros, contar todas
          const snapshot = await firstValueFrom(
            this.afs.collection('reserves').get()
          );
          this.totalReserves = snapshot.size;
        }
      }
      
      this.totalPages = Math.ceil(this.totalReserves / this.itemsPerPage);
    } catch (error) {
      console.error('Error al obtener el total de reservas:', error);
      this.totalReserves = 0;
      this.totalPages = 1;
    }
  }

  async loadPage(page: number): Promise<void> {
    if (page < 1) return;
    
    this.loaderService.showSpinner(`Cargando página ${page}...`);
    
    try {
      // Limpiar cursors cuando cambia el filtro (para evitar problemas de paginación)
      // Esto se hace automáticamente cuando se cambia el filtro en applyFilters()
      
      // Si el filtro es 'FAILED', necesitamos hacer dos consultas (FAILED y MINTED_FAILED)
      // y combinarlas, ya que Firestore no soporta OR directamente
      if (this.filterStatus === 'FAILED') {
        // Calcular cuántos documentos necesitamos de cada consulta
        const startIndex = (page - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        // Cargar más documentos para asegurar que tenemos suficientes después de combinar
        const limitPerQuery = Math.max(this.itemsPerPage * 3, endIndex + this.itemsPerPage);
        
        // Primera consulta para FAILED
        // Nota: Requiere índice compuesto en Firestore: status (Ascending) + timestamp (orden seleccionado)
        // Si hay filtro de projectId, requiere: projectId (Ascending) + status (Ascending) + timestamp (orden seleccionado)
        const failedQuery = this.afs.collection('reserves', ref => {
          let q: any = ref.where('status', '==', 'FAILED');
          if (this.filterProjectId.trim()) {
            q = q.where('projectId', '==', this.filterProjectId.trim());
          }
          return q.orderBy('timestamp', this.sortOrder).limit(limitPerQuery);
        });
        
        // Solo necesitamos una consulta para FAILED puras (sin MINTING_FAILED)
        let failedSnapshot: any;
        
        try {
          failedSnapshot = await firstValueFrom(failedQuery.get());
        } catch (error: any) {
          if (error.message && error.message.includes('index')) {
            console.error('Error: Se requiere un índice compuesto en Firestore');
            const directionLabel = this.sortOrder === 'desc' ? 'Descending' : 'Ascending';
            Swal.fire({
              title: 'Índice requerido',
              html: `
                <p>Firestore requiere un índice compuesto para esta consulta.</p>
                <p><strong>Colección:</strong> reserves</p>
                <p><strong>Campos:</strong></p>
                <ul style="text-align: left;">
                  <li>status (Ascending)</li>
                  <li>timestamp (${directionLabel})</li>
                </ul>
                <p>Haz clic en el enlace del error en la consola para crear el índice automáticamente, o créalo manualmente en la consola de Firebase.</p>
              `,
              icon: 'warning',
              confirmButtonText: 'Entendido'
            });
            this.loaderService.hideSpinner();
            return;
          }
          throw error;
        }
        
        // Filtrar failedSnapshot para obtener solo las que NO tienen reason MINTING_FAILED (son FAILED puras: ERROR, TIMEOUT, o sin reason)
        // Cuando el filtro es 'FAILED', NO incluimos las MINTED_FAILED (esas se muestran solo con el filtro 'MINTED_FAILED')
        const pureFailedDocs = failedSnapshot.docs.filter((doc: any) => {
          const data = doc.data() as any;
          return data.reason !== 'MINTING_FAILED';
        })
        .sort((a: any, b: any) => {
          const aData = a.data() as any;
          const bData = b.data() as any;
          const aTime = aData.timestamp || 0;
          const bTime = bData.timestamp || 0;
          return this.sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
        })
        .slice(startIndex, endIndex);
        
        // Crear un snapshot simulado con solo las FAILED puras (sin MINTED_FAILED)
        const snapshot = {
          docs: pureFailedDocs,
          size: pureFailedDocs.length
        };
        
        await this.processSnapshot(snapshot, page);
        return;
      }
      
      // Si el filtro es MINTED_FAILED, necesitamos buscar FAILED con reason MINTING_FAILED o ERROR
      if (this.filterStatus === 'MINTED_FAILED') {
        // Cargar todas las FAILED y filtrar después (ya que Firestore no soporta múltiples condiciones OR)
        const startIndex = (page - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        const limitPerQuery = Math.max(this.itemsPerPage * 3, endIndex + this.itemsPerPage);
        
        const failedQuery = this.afs.collection('reserves', ref => {
          let q: any = ref.where('status', '==', 'FAILED');
          if (this.filterProjectId.trim()) {
            q = q.where('projectId', '==', this.filterProjectId.trim());
          }
          return q.orderBy('timestamp', this.sortOrder).limit(limitPerQuery);
        });
        
        const failedSnapshot = await firstValueFrom(failedQuery.get());
        
        // Filtrar para obtener solo las que tienen reason MINTING_FAILED
        const mintedFailedDocs = failedSnapshot.docs.filter((doc: any) => {
          const data = doc.data() as any;
          return data.reason === 'MINTING_FAILED';
        })
        .slice(startIndex, endIndex);
        
        // Crear un snapshot simulado con los documentos filtrados
        const snapshot = {
          docs: mintedFailedDocs,
          size: mintedFailedDocs.length
        };
        
        await this.processSnapshot(snapshot, page);
        return;
      }
      
      // Para otros filtros, construir la consulta normal con el filtro aplicado
      let query = this.afs.collection('reserves', ref => {
        let q: any = ref.orderBy('timestamp', this.sortOrder);
        
        if (this.filterStatus !== 'all') {
          q = q.where('status', '==', this.filterStatus);
        }
        
        if (this.filterProjectId.trim()) {
          q = q.where('projectId', '==', this.filterProjectId.trim());
        }
        
        return q.limit(this.itemsPerPage);
      });
      
      // Si no es la primera página, usar startAfter con el cursor de la página anterior
      if (page > 1) {
        const previousPageCursor = this.pageCursors.get(page - 1);
        if (previousPageCursor) {
          query = this.afs.collection('reserves', ref => {
            let q: any = ref.orderBy('timestamp', this.sortOrder);
            
            if (this.filterStatus !== 'all') {
              q = q.where('status', '==', this.filterStatus);
            }
            
            if (this.filterProjectId.trim()) {
              q = q.where('projectId', '==', this.filterProjectId.trim());
            }
            
            return q.startAfter(previousPageCursor).limit(this.itemsPerPage);
          });
        } else {
          // Si no tenemos cursor de la página anterior, cargar desde el principio hasta esta página
          let currentCursor: any = null;
          
          for (let p = 1; p < page; p++) {
            let pageQuery = this.afs.collection('reserves', ref => {
              let q: any = ref.orderBy('timestamp', this.sortOrder);
              
              if (this.filterStatus !== 'all') {
                q = q.where('status', '==', this.filterStatus);
              }
              
              if (this.filterProjectId.trim()) {
                q = q.where('projectId', '==', this.filterProjectId.trim());
              }
              
              if (currentCursor) {
                return q.startAfter(currentCursor).limit(this.itemsPerPage);
              }
              return q.limit(this.itemsPerPage);
            });
            
            const pageSnapshot = await firstValueFrom(pageQuery.get());
            if (pageSnapshot.docs.length > 0) {
              currentCursor = pageSnapshot.docs[pageSnapshot.docs.length - 1];
              this.pageCursors.set(p, currentCursor);
            }
          }
          
          if (currentCursor) {
            query = this.afs.collection('reserves', ref => {
              let q: any = ref.orderBy('timestamp', this.sortOrder);
              
              if (this.filterStatus !== 'all') {
                q = q.where('status', '==', this.filterStatus);
              }
              
              if (this.filterProjectId.trim()) {
                q = q.where('projectId', '==', this.filterProjectId.trim());
              }
              
              return q.startAfter(currentCursor).limit(this.itemsPerPage);
            });
          }
        }
      }
      
      const snapshot = await firstValueFrom(query.get());
      await this.processSnapshot(snapshot, page);
    } catch (error: any) {
      console.error('Error al cargar página:', error);
      
      // Manejar error de índice faltante
      if (error.message && error.message.includes('index')) {
        const directionLabel = this.sortOrder === 'desc' ? 'Descending' : 'Ascending';
        Swal.fire({
          title: 'Índice requerido',
          html: `
            <p>Firestore requiere un índice compuesto para esta consulta.</p>
            <p><strong>Colección:</strong> reserves</p>
            <p><strong>Campos:</strong></p>
            <ul style="text-align: left;">
              <li>status (Ascending)</li>
              <li>timestamp (${directionLabel})</li>
            </ul>
            <p>Haz clic en el enlace del error en la consola para crear el índice automáticamente, o créalo manualmente en la consola de Firebase.</p>
          `,
          icon: 'warning',
          confirmButtonText: 'Entendido'
        });
      } else {
        Swal.fire('Error', 'Error al cargar las reservas: ' + (error.message || 'Desconocido'), 'error');
      }
      
      this.loaderService.hideSpinner();
    }
  }

  private mapDocToReserve(doc: any): ReserveData {
    const data = doc.data() as any;
    
    let actualStatus = data.status || 'PENDIENTE';
    if (data.status === 'FAILED' && data.reason === 'MINTING_FAILED') {
      actualStatus = 'MINTED_FAILED';
    }
    
    return {
      id: doc.id,
      userId: data.userId || '',
      projectId: data.projectId || '',
      shares: data.shares || 0,
      amount: typeof data.amount === 'number' ? data.amount : 0,
      timestamp: data.timestamp || Date.now(),
      status: actualStatus as 'PENDIENTE' | 'MINTING' | 'FAILED' | 'MINTED' | 'MINTED_FAILED',
      reason: data.reason,
      errorMessage: data.errorMessage,
      projectName: undefined,
      priceToken: undefined,
      transferId: data.transferId,
      isExternal: data.isExternal || false,
      projectWallet: data.projectWallet,
      userWallet: data.userWallet,
      userEmail: 'Cargando...',
      adminNotes: data.adminNotes || '',
      showDetails: false
    };
  }

  async processSnapshot(snapshot: any, page: number): Promise<void> {
    try {
      const reservesData: ReserveData[] = [];
      
      snapshot.docs.forEach((doc: any) => {
        const cachedReserve = this.loadedReservesCache.get(doc.id);
        if (cachedReserve) {
          reservesData.push(cachedReserve);
          return;
        }
        const reserve = this.mapDocToReserve(doc);
        reservesData.push(reserve);
        this.loadedReservesCache.set(doc.id, reserve);
      });
      
      // Guardar el cursor del último documento de esta página para la siguiente
      if (snapshot.docs.length > 0) {
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        this.pageCursors.set(page, lastDoc);
      }
      
      // Aplicar filtros a las reservas cargadas
      let filtered = this.applyFiltersToReserves(reservesData);
      
      // Si después de filtrar no hay suficientes reservas y hay más páginas, cargar más
      if (filtered.length < this.itemsPerPage && page < this.totalPages && snapshot.docs.length === this.itemsPerPage) {
        // Cargar la siguiente página también
        const nextPageCursor = this.pageCursors.get(page);
        if (nextPageCursor) {
          let nextPageQuery = this.afs.collection('reserves', ref => {
            let q: any = ref.orderBy('timestamp', this.sortOrder);
            
            if (this.filterStatus !== 'all') {
              if (this.filterStatus === 'FAILED') {
                // Para FAILED no podemos usar startAfter con el filtro, así que cargamos sin filtro
                q = q.startAfter(nextPageCursor);
              } else {
                q = q.where('status', '==', this.filterStatus).startAfter(nextPageCursor);
              }
            } else {
              q = q.startAfter(nextPageCursor);
            }
            
            if (this.filterProjectId.trim()) {
              q = q.where('projectId', '==', this.filterProjectId.trim());
            }
            
            return q.limit(this.itemsPerPage);
          });
          
          const nextPageSnapshot = await firstValueFrom(nextPageQuery.get());
          
          nextPageSnapshot.docs.forEach((doc: any) => {
            if (!this.loadedReservesCache.has(doc.id)) {
              const reserve = this.mapDocToReserve(doc);
              reservesData.push(reserve);
              this.loadedReservesCache.set(doc.id, reserve);
            }
          });
          
          // Guardar cursor de la siguiente página
          if (nextPageSnapshot.docs.length > 0) {
            const lastDoc = nextPageSnapshot.docs[nextPageSnapshot.docs.length - 1];
            this.pageCursors.set(page + 1, lastDoc);
          }
          
          filtered = this.applyFiltersToReserves(reservesData);
        }
      }
      
      this.paginatedReserves = filtered.slice(0, this.itemsPerPage);
      this.loaderService.hideSpinner();
      
      // Cargar datos adicionales (emails, proyectos) en segundo plano
      this.loadAdditionalDataInBackground(this.paginatedReserves);
      
      // Reinicializar tooltips después de actualizar los datos
      setTimeout(() => {
        this.initializeTooltips();
      }, 200);
    } catch (error: any) {
      console.error('Error al procesar snapshot:', error);
      Swal.fire('Error', 'Error al procesar las reservas: ' + (error.message || 'Desconocido'), 'error');
      this.loaderService.hideSpinner();
    }
  }

  applyFiltersToReserves(reserves: ReserveData[]): ReserveData[] {
    let filtered = [...reserves];

    // Filtrar reservas con monto 0: 
    // - MINTED con amount 0: NO mostrar (no tiene sentido una reserva completada con 0 euros)
    // - PENDIENTE con amount 0: SÍ mostrar (puede estar pendiente de cálculo o ser válida)
    // - FAILED con amount 0: SÍ mostrar (importante ver las fallidas aunque sean 0)
    // Solo aplicar este filtro si el priceToken ya se ha cargado (para evitar filtrar reservas que aún no tienen el monto calculado)
    filtered = filtered.filter(reserve => {
      // Si el priceToken no se ha cargado aún, no filtrar por amount (mostrar todas)
      if (reserve.priceToken === undefined || reserve.priceToken === null) {
        return true;
      }
      // Si el priceToken ya se cargó y el amount es 0, solo mostrar PENDIENTE, FAILED o MINTED_FAILED
      if (reserve.amount === 0 || reserve.amount === null || reserve.amount === undefined) {
        // Excluir MINTED con amount 0, pero incluir PENDIENTE, FAILED y MINTED_FAILED
        const shouldKeep = reserve.status === 'PENDIENTE' || reserve.status === 'FAILED' || reserve.status === 'MINTED_FAILED';
        return shouldKeep;
      }
      return true;
    });

    // Filtro por UID
    if (this.filterUID.trim()) {
      filtered = filtered.filter(reserve =>
        reserve.userId.toLowerCase().includes(this.filterUID.toLowerCase())
      );
    }

    // Filtro por ProjectId (aplicado a nivel de servidor, pero también aquí por seguridad)
    if (this.filterProjectId.trim()) {
      filtered = filtered.filter(reserve =>
        reserve.projectId === this.filterProjectId.trim()
      );
    }

    // Filtro por monto mínimo
    if (this.filterMinAmount !== null && this.filterMinAmount > 0) {
      filtered = filtered.filter(reserve =>
        reserve.amount >= this.filterMinAmount!
      );
    }

    // Filtro por monto máximo
    if (this.filterMaxAmount !== null && this.filterMaxAmount > 0) {
      filtered = filtered.filter(reserve =>
        reserve.amount <= this.filterMaxAmount!
      );
    }

    // Filtro por estado
    if (this.filterStatus !== 'all') {
      filtered = filtered.filter(reserve => {
        if (this.filterStatus === 'FAILED') {
          return reserve.status === 'FAILED';
        }
        return reserve.status === this.filterStatus;
      });
    }
    
    return this.sortReserves(filtered);
  }

  private sortReserves(reserves: ReserveData[]): ReserveData[] {
    const sorted = [...reserves];
    sorted.sort((a, b) => {
      const aTime = a?.timestamp || 0;
      const bTime = b?.timestamp || 0;
      return this.sortOrder === 'desc' ? bTime - aTime : aTime - bTime;
    });
    return sorted;
  }

  private async hydrateReserveMetadata(reserve: ReserveData): Promise<void> {
    if (reserve.userId) {
      try {
        reserve.userEmail = await this.getUserEmail(reserve.userId);
      } catch (error) {
        console.error('Error al obtener email para userId:', reserve.userId, error);
        reserve.userEmail = 'Error al obtener email';
      }
    } else {
      reserve.userEmail = 'Sin UID';
    }
    
    if (reserve.projectId) {
      try {
        const projectInfo = await this.getProjectInfo(reserve.projectId);
        reserve.projectName = projectInfo.name;
        reserve.priceToken = projectInfo.priceToken;
        
        if (projectInfo.priceToken && reserve.shares) {
          reserve.amount = Number(((reserve.shares * projectInfo.priceToken) / 100).toFixed(2));
        }
      } catch (error) {
        console.error('Error al obtener proyecto para projectId:', reserve.projectId, error);
        reserve.projectName = undefined;
        reserve.priceToken = undefined;
      }
    }
  }

  async loadAdditionalDataInBackground(reserves: ReserveData[]): Promise<void> {
    for (const reserve of reserves) {
      await this.hydrateReserveMetadata(reserve);
      
      this.loadedReservesCache.set(reserve.id, { ...reserve });
      const index = this.paginatedReserves.findIndex(r => r.id === reserve.id);
      if (index !== -1) {
        this.paginatedReserves[index] = { ...reserve };
      }
      this.cdr.detectChanges();
    }
    
    setTimeout(() => {
      this.initializeTooltips();
    }, 100);
    
    this.paginatedReserves = this.paginatedReserves.filter(reserve => {
      if (reserve.priceToken !== undefined && reserve.priceToken !== null) {
        if ((reserve.amount === 0 || reserve.amount === null || reserve.amount === undefined) 
            && reserve.status === 'MINTED') {
          return false;
        }
      }
      return true;
    });
    this.cdr.detectChanges();
  }

  async getUserEmail(userId: string): Promise<string> {
    // Verificar cache primero
    if (this.userEmailsCache.has(userId)) {
      return this.userEmailsCache.get(userId) || '';
    }
    
    try {
      const userDoc = await firstValueFrom(
        this.afs.collection('users').doc(userId).get()
      );
      
      if (userDoc?.exists) {
        const userData = userDoc.data() as any;
        const email = userData.email || 'Email no disponible';
        this.userEmailsCache.set(userId, email);
        return email;
      }
      return 'Usuario no encontrado';
    } catch (error) {
      console.error('Error al obtener email del usuario:', userId, error);
      return 'Error al obtener email';
    }
  }


  async getProjectInfo(projectId: string): Promise<{ name: string | undefined; priceToken: number | undefined }> {
    // Verificar cache primero
    if (this.projectInfoCache.has(projectId)) {
      return this.projectInfoCache.get(projectId)!;
    }
    
    try {
      const projectDoc = await firstValueFrom(
        this.afs.collection('investments').doc(projectId).get()
      );
      
      if (projectDoc?.exists) {
        const projectData = projectDoc.data() as any;
        const projectName = projectData.nameTokens || projectData.title;
        const priceToken = projectData.priceToken;
        const result = { name: projectName, priceToken: priceToken };
        this.projectInfoCache.set(projectId, result);
        return result;
      }
      return { name: undefined, priceToken: undefined };
    } catch (error) {
      console.error(`Error al obtener información del proyecto ${projectId}:`, error);
      return { name: undefined, priceToken: undefined };
    }
  }

  async applyFilters(): Promise<void> {
    // Limpiar cursors cuando cambia el filtro para evitar problemas de paginación
    this.pageCursors.clear();
    // Recalcular el total de reservas con el nuevo filtro
    await this.loadTotalCount();
    // Recargar la página actual con los filtros aplicados
    this.currentPage = 1;
    await this.loadPage(this.currentPage);
  }

  async onSortOrderChange(order: 'asc' | 'desc'): Promise<void> {
    if (this.sortOrder === order) {
      return;
    }
    this.sortOrder = order;
    await this.applyFilters();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Los tooltips se reinicializarán automáticamente después de cargar la página
    }
  }

  clearFilters(): void {
    this.filterUID = '';
    this.filterProjectId = '';
    this.filterMinAmount = null;
    this.filterMaxAmount = null;
    this.filterStatus = 'all';
    this.sortOrder = 'desc';
    this.currentPage = 1;
    this.loadPage(1);
  }

  transformDate(timestamp: number | undefined | null): string {
    try {
      if (!timestamp || timestamp === 0) {
        return 'Fecha no disponible';
      }
      
      const date = new Date(timestamp);
      
      if (isNaN(date.getTime())) {
        return 'Fecha inválida';
      }
      
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error al transformar fecha:', error);
      return 'Error en fecha';
    }
  }

  getReserveTimestamp(reserve: ReserveData | undefined): number {
    if (!reserve || reserve.timestamp === undefined || reserve.timestamp === null) {
      return 0;
    }
    return reserve.timestamp as number;
  }

  formatAmount(amount: number): string {
    return amount.toFixed(2);
  }

  private formatTimestampToCET(timestamp: number): string {
    try {
      if (!timestamp) {
        return 'Fecha no disponible';
      }
      const formatter = new Intl.DateTimeFormat('es-ES', {
        timeZone: 'Europe/Madrid',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return formatter.format(new Date(timestamp));
    } catch (error) {
      console.error('Error al formatear timestamp CET:', error);
      return 'Error en fecha';
    }
  }

  async downloadExcel(): Promise<void> {
    if (!this.filterProjectId.trim() || this.filterStatus === 'all') {
      Swal.fire(
        'Filtros requeridos',
        'Selecciona un Project ID y un estado específico antes de exportar.',
        'info'
      );
      return;
    }
    
    this.loaderService.showSpinner('Generando Excel de reservas...');
    
    try {
      const snapshot = await firstValueFrom(
        this.afs.collection('reserves', ref => {
          let q: any = ref.where('projectId', '==', this.filterProjectId.trim());
          
          if (this.filterStatus === 'FAILED' || this.filterStatus === 'MINTED_FAILED') {
            q = q.where('status', '==', 'FAILED');
          } else {
            q = q.where('status', '==', this.filterStatus);
          }
          
          return q.orderBy('timestamp', this.sortOrder);
        }).get()
      );
      
      let reserves = snapshot.docs.map((doc: any) => this.mapDocToReserve(doc));
      
      if (this.filterStatus === 'FAILED') {
        reserves = reserves.filter(reserve => reserve.reason !== 'MINTING_FAILED');
      } else if (this.filterStatus === 'MINTED_FAILED') {
        reserves = reserves.filter(reserve => reserve.reason === 'MINTING_FAILED');
      }
      
      reserves = this.applyFiltersToReserves(reserves);
      
      if (!reserves.length) {
        Swal.fire('Sin datos', 'No se encontraron reservas para exportar con los filtros seleccionados.', 'info');
        return;
      }
      
      await Promise.all(reserves.map(reserve => this.hydrateReserveMetadata(reserve)));
      
      const excelData = reserves.map((reserve, index) => {
        const priceInEuros = reserve.priceToken ? reserve.priceToken / 100 : undefined;
        return {
          '#': index + 1,
          UID: reserve.userId,
          Email: reserve.userEmail || '',
          ProjectId: reserve.projectId,
          Proyecto: reserve.projectName || '',
          Estado: this.getStatusLabel(reserve.status),
          Motivo: reserve.reason ? this.getReasonLabel(reserve.reason) : '',
          Tokens: reserve.shares,
          'Precio token (€)': priceInEuros !== undefined ? Number(priceInEuros.toFixed(2)) : '',
          'Importe (€)': Number((reserve.amount ?? 0).toFixed(2)),
          'Timestamp (ms)': reserve.timestamp,
          'Fecha ISO': new Date(reserve.timestamp).toISOString(),
          'Fecha Europa Central': this.formatTimestampToCET(reserve.timestamp),
          'Transfer ID': reserve.transferId || '',
          'Wallet usuario': reserve.userWallet || '',
          'Wallet proyecto': reserve.projectWallet || '',
          'Notas admin': reserve.adminNotes || ''
        };
      });
      
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Reservas');
      
      const sanitizedProjectId = this.filterProjectId.trim().replace(/[^a-zA-Z0-9-_]/g, '_');
      const timestampLabel = new Date().toISOString().replace(/[:.]/g, '-');
      const fileName = `reservas_${sanitizedProjectId}_${this.filterStatus}_${timestampLabel}.xlsx`;
      
      XLSX.writeFile(workbook, fileName);
      Swal.fire('Excel generado', 'La descarga se inició correctamente.', 'success');
    } catch (error) {
      console.error('Error al generar Excel de reservas:', error);
      Swal.fire('Error', 'No se pudo generar el Excel. Intenta nuevamente.', 'error');
    } finally {
      this.loaderService.hideSpinner();
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'MINTED': return 'bg-success';
      case 'FAILED': return 'bg-danger';
      case 'MINTED_FAILED': return 'bg-danger';
      case 'PENDIENTE': return 'bg-warning';
      case 'MINTING': return 'bg-info';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'MINTED': return 'Completada';
      case 'FAILED': return 'Fallida';
      case 'MINTED_FAILED': return 'Fallida (Mint)';
      case 'PENDIENTE': return 'Pendiente';
      case 'MINTING': return 'Minteando';
      default: return status;
    }
  }

  getReasonLabel(reason?: string): string {
    switch (reason) {
      case 'MINTING_FAILED': return 'Error en el Minteo de Tokens';
      case 'ERROR': return 'Error en el Pago';
      case 'TIMEOUT': return 'Tiempo de Espera Agotado';
      default: return 'Error Desconocido';
    }
  }

  getReasonExplanation(reason?: string): string {
    switch (reason) {
      case 'MINTING_FAILED':
        return 'El pago se realizó correctamente, pero hubo un problema al crear los tokens en la blockchain.';
      case 'ERROR':
        return 'Ocurrió un problema durante el proceso de pago. La reserva no se completó y no se cobró ningún importe.';
      case 'TIMEOUT':
        return 'La reserva expiró porque no se completó dentro del tiempo límite establecido.';
      default:
        return 'Ocurrió un error durante el proceso.';
    }
  }

  getTooltipContent(reserve: ReserveData): string {
    if (!reserve.reason) return '';
    
    let content = `<strong>${this.getReasonLabel(reserve.reason)}</strong><br>`;
    content += `<small>${this.getReasonExplanation(reserve.reason)}</small>`;
    
    if (reserve.errorMessage) {
      content += `<br><br><strong>Detalles técnicos:</strong><br>`;
      content += `<small style="font-family: monospace;">${reserve.errorMessage}</small>`;
    }
    
    return content;
  }

  onStatusChange(event: Event, reserveId: string): void {
    const target = event.target as HTMLSelectElement;
    const newStatus = target.value as 'PENDIENTE' | 'FAILED' | 'MINTED' | 'MINTED_FAILED';
    this.updateReserveStatus(reserveId, newStatus);
  }

  updateReserveStatus(reserveId: string, newStatus: 'PENDIENTE' | 'FAILED' | 'MINTED' | 'MINTED_FAILED'): void {
    // Una reserva ya minteada nunca debe volver a PENDIENTE: el cron la recogería y
    // volvería a mintear tokens ya emitidos en blockchain (doble minteo + descuadre de reserves)
    const currentReserve = this.loadedReservesCache.get(reserveId);
    if (currentReserve?.status === 'MINTED' && newStatus === 'PENDIENTE') {
      Swal.fire({
        icon: 'error',
        title: 'Acción no permitida',
        text: 'Esta reserva ya fue minteada. Devolverla a Pendiente haría que el sistema vuelva a mintear tokens ya emitidos.'
      });
      this.loadPage(this.currentPage);
      return;
    }
    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas cambiar el estado de esta reserva a ${this.getStatusLabel(newStatus)}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#dc3545',
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.loaderService.showSpinner('Actualizando estado de la reserva...');
        
        this.afs.collection('reserves').doc(reserveId).update({
          status: newStatus,
          updatedAt: Date.now()
        }).then(() => {
          Swal.fire('Actualizado', 'El estado de la reserva ha sido actualizado', 'success');
          this.loaderService.hideSpinner();
        }).catch((error: any) => {
          console.error('Error al actualizar reserva:', error);
          Swal.fire('Error', 'Error al actualizar el estado de la reserva', 'error');
          this.loaderService.hideSpinner();
        });
      }
    });
  }

  saveAdminNotes(reserveId: string, notes: string): void {
    this.afs.collection('reserves').doc(reserveId).update({
      adminNotes: notes,
      notesUpdatedAt: Date.now()
    }).then(() => {
      // Actualizar en cache y vista
      const reserve = this.loadedReservesCache.get(reserveId);
      if (reserve) {
        reserve.adminNotes = notes;
        this.loadedReservesCache.set(reserveId, reserve);
      }
      const index = this.paginatedReserves.findIndex(r => r.id === reserveId);
      if (index !== -1) {
        this.paginatedReserves[index].adminNotes = notes;
      }
    }).catch((error: any) => {
      console.error('Error al guardar notas:', error);
      Swal.fire('Error', 'Error al guardar las notas', 'error');
    });
  }

  onNotesBlur(reserveId: string, event: any): void {
    const notes = event.target.value || '';
    this.saveAdminNotes(reserveId, notes);
  }

  toggleDetails(reserve: ReserveData): void {
    reserve.showDetails = !reserve.showDetails;
    // Actualizar en cache
    this.loadedReservesCache.set(reserve.id, { ...reserve });
    // Forzar actualización de la vista
    const index = this.paginatedReserves.findIndex(r => r.id === reserve.id);
    if (index !== -1) {
      this.paginatedReserves[index] = { ...reserve };
    }
  }

  async processReserveInvestment(reserve: ReserveData): Promise<void> {
    // Protección contra doble click: ignorar si ya está en proceso
    if (this.processingReserveIds.has(reserve.id)) {
      return;
    }

    // Solo procesar reservas con estado MINTED_FAILED
    if (reserve.status !== 'MINTED_FAILED') {
      Swal.fire({
        icon: 'warning',
        title: 'Acción no permitida',
        text: 'Solo se puede mintear reservas con estado "Fallida (Mint)".'
      });
      return;
    }

    // Confirmar acción
    const confirmResult = await Swal.fire({
      title: 'Mintear Tokens',
      html: `¿Estás seguro de que quieres registrar la inversión y hacer el minteo para esta reserva?<br><br>
        <strong>Usuario:</strong> ${reserve.userEmail || reserve.userId}<br>
        <strong>Proyecto:</strong> ${reserve.projectName || reserve.projectId}<br>
        <strong>Tokens:</strong> ${reserve.shares}<br>
        <strong>Monto:</strong> ${this.formatAmount(reserve.amount)} €`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, mintear',
      cancelButtonText: 'Cancelar'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    this.processingReserveIds.add(reserve.id);

    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      // Buscar el usuario por userId
      const userDoc = await firstValueFrom(
        this.afs.collection('users').doc(reserve.userId).get()
      );

      if (!userDoc || !userDoc.exists) {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Usuario no encontrado'
        });
        return;
      }

      const userData: any = userDoc.data();

      // Verificar KYC
      if (userData?.kyc?.result != 1) {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'El usuario no tiene KYC completado'
        });
        return;
      }

      // Verificar país de residencia
      if (
        (!userData?.particular || !userData?.particular?.pais_de_residencia) &&
        !userData?.Empresa?.Representante?.pais_de_residencia
      ) {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'El usuario presenta irregularidades en la verificación'
        });
        return;
      }

      // Crear wallet si no existe
      const walletResult: any = await firstValueFrom(
        this.fns.httpsCallable('newWalletByCompany')({ userUid: reserve.userId })
      );

      if (!walletResult.success) {
        Swal.close();
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Error al crear wallet: ${walletResult.error || 'Error desconocido'}`
        });
        return;
      }

      // Devolver la reserva a PENDIENTE: el cron del backend la recogerá, verificará el
      // pago original por su transferId (sin recobrar) y reintentará el minteo vía
      // mintTokenWithoutPay, que ya deduplica la transacción por reserveId.
      await this.afs.collection('reserves').doc(reserve.id).update({
        status: 'PENDIENTE',
        updatedAt: Date.now()
      });

      // Actualizar en cache y vista
      reserve.status = 'PENDIENTE';
      this.loadedReservesCache.set(reserve.id, { ...reserve });
      const index = this.paginatedReserves.findIndex(r => r.id === reserve.id);
      if (index !== -1) {
        this.paginatedReserves[index] = { ...reserve };
      }

      Swal.close();
      Swal.fire({
        title: 'Reserva encolada',
        text: 'La reserva volvió a Pendiente. El sistema verificará el pago original y reintentará el minteo automáticamente en el próximo ciclo (aprox. 1 minuto).',
        icon: 'success'
      });

      // Recargar la página actual para reflejar los cambios
      await this.loadPage(this.currentPage);
    } catch (error: any) {
      console.error('Error al procesar inversión:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Error al procesar la inversión: ${error.message || 'Error desconocido'}`
      });
    } finally {
      this.processingReserveIds.delete(reserve.id);
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  hasMintedFailedInCurrentPage(): boolean {
    return this.paginatedReserves.some(reserve => reserve.status === 'MINTED_FAILED');
  }
}

